import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { faCalendarAlt } from '@fortawesome/pro-duotone-svg-icons/faCalendarAlt';
import { faMapMarkerAlt } from '@fortawesome/pro-duotone-svg-icons/faMapMarkerAlt';

import { Avatar } from 'us-react-components/dist/Avatar/Avatar';
import { JobAvailabilityType } from 'us-web-services';
import { NuxContainer } from 'us-react-components/dist/NuxContainer/NuxContainer';
import { useDependencies } from '../../../dependencies';
import { FlowType } from '../../../models/FamilyRegistrationStore/constants';
import { Routes } from '../../../Routes';
import { DependentsSummary } from './components/DependentsSummary';
import { IconText } from './components/IconText';
import { RecurringSummary } from './components/RecurringSummary';

const AVATAR_URL = 'https://assets.urbansitter.com/static/img/empty-avatar.svg';

export const ProfilePreview = observer((): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { stores: { familyRegistration, user }, services: { tracker } } = useDependencies();

  familyRegistration.setCurrentRoute(Routes.parent.registration.profilePreview);


  const getSummaryTitle = (): string => {
    const frequency = ((): string => {
      switch (familyRegistration.availabilityType) {
        case JobAvailabilityType.fullTime:
          return t('common.frequencies.fullTime');
        case JobAvailabilityType.partTime:
          return t('common.frequencies.partTime');
        default:
          return t('common.frequencies.specificDate');
      }
    })();

    const vertical = ((): string => {
      switch (familyRegistration.flowType) {
        case FlowType.SeniorCare:
          return t('common.verticals.seniorCare');
        case FlowType.PetCareRecurring:
          return t('common.verticals.petCare');
        case FlowType.HouseholdServices:
          return t('common.verticals.householdServices');
        default:
          return t('common.verticals.childcare');
      }
    })();

    return `${frequency.toUpperCase()} ${vertical}`;
  }

  const handleContinue = (): void => {
    if (familyRegistration.flowType === FlowType.ChildCare) {
      navigate(Routes.parent.registration.includes);
      return;
    }
    navigate(Routes.parent.registration.details);
  }

  const getStartDisplay = (): string => {
    if (familyRegistration.startDate && familyRegistration.availabilityType !== JobAvailabilityType.occasional) {
      return familyRegistration.startDate.toLocaleDateString('en-US');
    }

    return `${familyRegistration.startDate?.toLocaleDateString('en-US')} ${familyRegistration.startTimeDisplay}`;
  }

  const getEndDisplay = (): string => {
    if (familyRegistration.availabilityType === JobAvailabilityType.occasional) {
      if (!familyRegistration.endDate) {
        return `${familyRegistration.startDate?.toLocaleDateString('en-US')} ${familyRegistration.endTimeDisplay}`;
      }

      return `${familyRegistration.endDate?.toLocaleDateString('en-US')} ${familyRegistration.endTimeDisplay}`;
    }

    return familyRegistration.endDate?.toLocaleDateString('en-US') || '';
  }

  return (
    <div className='relative'>
      <NuxContainer
        title={t('pages.nux.family.profile.title')}
        familyRegistration={familyRegistration}
        navigate={navigate}
        tracker={tracker}
        continueTitle={t('pages.nux.family.profile.button')}
        onContinue={handleContinue}
        invertedTitle
        headerBackground
      >
        <div className='z-10 border-[1px] border-emerald-300 flex flex-col w-[90%] md:w-[70%] my-6 bg-white rounded-xl p-4 sm:p-6'>
          <div className='flex pb-2'>
            <Avatar className='border-[1px] border-emerald w-[70px] h-[70px] xs:w-[90px] xs:h-[90px] sm:w-[110px] sm:h-[110px] mr-2' imageUrl={user.user?.pictureUrl ?? AVATAR_URL} />
            <div className='pl-4 w-[200px] lg:w-[350px]'>
              <p className='text-xl font-semibold xs:pb-2 sm:pb-3'>{user.user.firstName} {user.user.lastName?.substring(0, 1)}.</p>
              <IconText icon={faMapMarkerAlt} text={`${familyRegistration.profile.address?.city}, ${familyRegistration.profile.address?.state} ${familyRegistration.profile.address?.zip}`} />
              <DependentsSummary />
            </div>
          </div>
          <p className='font-semibold xs:py-4 sm:py-4 sm:text-lg text-center'>{getSummaryTitle()}</p>
          <div className='w-full sm:w-[70%]'>
            {
              familyRegistration.startDate &&
              (
                <IconText icon={faCalendarAlt} text={t('pages.nux.family.profile.startDate', { startDate: getStartDisplay() })} />
              )
            }
            {
              ((familyRegistration.startDate && familyRegistration.availabilityType === JobAvailabilityType.occasional) ||
                familyRegistration.endDate) &&
              (
                <IconText icon={faCalendarAlt} text={t('pages.nux.family.profile.endDate', { endDate: getEndDisplay() })} />
              )
            }
            {
              familyRegistration.availabilityType !== JobAvailabilityType.occasional &&
              (
                <RecurringSummary />
              )
            }
          </div>
        </div>
      </NuxContainer>
    </div>
  )
});
